import React from 'react'
import { useNavigate } from 'react-router-dom'
import { formatStatus } from '../../../constants/leagueStatuses'
import './LeaguesTable.css'

const getStatusBadgeClass = (status) => {
  const statusClasses = {
    draft: 'bg-secondary',
    registration_open: 'bg-info',
    registration_closed: 'bg-warning',
    pending_start: 'bg-primary',
    active: 'bg-success',
    in_progress: 'bg-success',
    playoffs_setup: 'bg-warning',
    playoffs: 'bg-purple',
    completed: 'bg-dark',
    canceled: 'bg-danger',
  }
  return statusClasses[status] || 'bg-secondary'
}

/**
 * LeaguesTable - Displays all leagues with action buttons
 *
 * @param {Object} props
 * @param {Array} props.leagues - Array of league objects
 * @param {number|null} props.selectedLeagueId - Currently selected league ID
 * @param {boolean} props.isDownloadingCsv - Whether CSV download is in progress
 * @param {Function} props.onSelectLeague - Handler when Manage button is clicked
 * @param {Function} props.onDownloadCsv - Handler for CSV download
 * @param {Function} props.onDelete - Handler for league deletion
 * @param {Function} props.getDeleteButtonProps - Function to get delete button props
 * @returns {JSX.Element}
 */
function LeaguesTable({
  leagues,
  selectedLeagueId,
  isDownloadingCsv,
  onSelectLeague,
  onDownloadCsv,
  onDelete,
  getDeleteButtonProps,
}) {
  const navigate = useNavigate()

  return (
    <div className="table-responsive">
      <table className="table mb-4 admin-leagues-table">
        <thead>
          <tr>
            <th scope="col">League Name</th>
            <th scope="col">Type</th>
            <th scope="col">Region</th>
            <th scope="col">Status</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {leagues.map((league) => (
            <tr
              key={league.id}
              className={selectedLeagueId === league.id ? 'table-active' : ''}
            >
              <td data-label="League Name">{league.name}</td>
              <td data-label="Type">{league.league_type}</td>
              <td data-label="Region">{league.region?.city_area || '—'}</td>
              <td data-label="Status">
                <span
                  className={`badge ${getStatusBadgeClass(
                    league.league_status,
                  )}`}
                >
                  {formatStatus(league.league_status)}
                </span>
              </td>
              <td data-label="Actions">
                <div className="admin-leagues-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => onSelectLeague(league.id)}
                    data-track="admin.manage_league"
                    aria-label={`Manage ${league.name}`}
                  >
                    Manage
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() =>
                      navigate(`/admin/leagues/editor/${league.id}`)
                    }
                    data-track="admin.edit_league"
                    aria-label={`Edit ${league.name}`}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => onDownloadCsv(league.id)}
                    disabled={isDownloadingCsv}
                    title="Download contact list (CSV)"
                    data-track="admin.download_participants_csv"
                    aria-label={`Download contact list for ${league.name}`}
                  >
                    {isDownloadingCsv ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-download me-1"></i>
                        Contact List
                      </>
                    )}
                  </button>
                  <button
                    {...getDeleteButtonProps(league)}
                    onClick={() => onDelete(league)}
                    data-track="admin.delete_league"
                    aria-label={`Delete ${league.name}`}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default LeaguesTable
